import React, { useEffect, useState } from "react";
import "./settings.scss";
import { getSettings, updateSettings } from "../../api/crawlerApi";

export default function SettingsPage() {
  const [form, setForm] = useState({
    emsSyncStatus: true,
    consolidateInterval: 24,
    newsValidPeriod: 180,
    siteMaintenance: false,
    clientBusinessNature: "",
    mybinaEnabled: true,
    mybinaUsername: "",
    mybinaPassword: "",
    mybinaPasswordConfigured: false,
    mybinaSyncStatus: "pending",
    mybinaLastSyncAt: "Not synced yet",
  });
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getSettings()
      .then((data) => {
        const system = data.system || data;
        const mybina = data.mybina || data.mybina_credential || {};

        setForm((prev) => ({
          ...prev,
          emsSyncStatus: Boolean(system.ems_sync_enabled),
          consolidateInterval: system.consolidate_interval_hours || 24,
          newsValidPeriod: system.news_valid_days || 180,
          siteMaintenance: Boolean(system.site_maintenance_enabled),
          clientBusinessNature: system.client_business_nature || "",
          mybinaEnabled: mybina.enabled !== false,
          mybinaUsername: mybina.username || "",
          mybinaPasswordConfigured: Boolean(mybina.password_configured),
          mybinaSyncStatus: mybina.sync_status || "pending",
          mybinaLastSyncAt: mybina.last_synced_at || "Not synced yet",
        }));
      })
      .catch((err) => {
        setMessage(err.message || "Failed to load settings.");
      });
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const settingsPayload = () => ({
    ems_sync_enabled: form.emsSyncStatus,
    consolidate_interval_hours: Number(form.consolidateInterval),
    news_valid_days: Number(form.newsValidPeriod),
    site_maintenance_enabled: form.siteMaintenance,
    client_business_nature: form.clientBusinessNature.trim(),
    mybina: {
      enabled: form.mybinaEnabled,
      username: form.mybinaUsername.trim(),
      password: form.mybinaPassword,
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSaving(true);

    try {
      const saved = await updateSettings(settingsPayload());

      setForm((prev) => ({
        ...prev,
        mybinaPassword: "",
        mybinaPasswordConfigured: Boolean(saved.mybina?.password_configured),
      }));
      setMessage("Settings saved successfully.");
    } catch (err) {
      setMessage(err.message || "Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="settings-page">
      <form onSubmit={handleSubmit}>
        {message && <div className="settings-card">{message}</div>}

        <div className="settings-card">
          <div className="settings-card__header">
            <h2>System Settings</h2>
            <p>
              Configure EMS synchronization, AI consolidation schedule,
              and maintenance mode.
            </p>
          </div>

          <div className="setting-row">
            <div className="setting-row__content">
              <h3>EMS Sync Status</h3>
              <p>Enable or disable synchronization between YKMW Crawler and EMS.</p>
            </div>

            <label className="switch">
              <input
                type="checkbox"
                checked={form.emsSyncStatus}
                onChange={(e) => handleChange("emsSyncStatus", e.target.checked)}
              />
              <span className="slider" />
            </label>
          </div>

          <div className="setting-row">
            <div className="setting-row__content">
              <h3>Consolidate Interval</h3>
              <p>Number of hours between AI lead consolidation runs.</p>
            </div>

            <div className="hours-input">
              <input
                type="number"
                min="1"
                value={form.consolidateInterval}
                onChange={(e) => handleChange("consolidateInterval", Number(e.target.value))}
              />
              <span>Hours</span>
            </div>
          </div>

          <div className="setting-row">
            <div className="setting-row__content">
              <h3>News Valid Period</h3>
              <p>Number of days crawled news remains valid for consolidation and lead generation.</p>
            </div>

            <div className="hours-input">
              <input
                type="number"
                min="1"
                value={form.newsValidPeriod}
                onChange={(e) => handleChange("newsValidPeriod", Number(e.target.value))}
              />
              <span>Days</span>
            </div>
          </div>

          <div className="setting-row">
            <div className="setting-row__content">
              <h3>Site Maintenance</h3>
              <p>Enable maintenance mode to temporarily block user access.</p>
            </div>

            <label className="switch">
              <input
                type="checkbox"
                checked={form.siteMaintenance}
                onChange={(e) => handleChange("siteMaintenance", e.target.checked)}
              />
              <span className="slider" />
            </label>
          </div>

          <div className="setting-row setting-row--stacked">
            <div className="setting-row__content">
              <h3>Client Business Nature</h3>
              <p>Used by AI Score to judge whether a lead is commercially relevant to the client business.</p>
            </div>

            <label className="settings-textarea">
              <textarea
                rows={4}
                value={form.clientBusinessNature}
                onChange={(e) => handleChange("clientBusinessNature", e.target.value)}
                placeholder="Example: Client supplies metal mesh for construction projects and fencing works."
              />
            </label>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card__header settings-card__header--with-status">
            <div>
              <h2>myBina Credential Settings</h2>
              <p>Configure the myBina account used by the crawler to download project data and reports.</p>
            </div>

            <span className={`connection-badge connection-badge--${String(form.mybinaSyncStatus).toLowerCase()}`}>
              {form.mybinaSyncStatus}
            </span>
          </div>

          <div className="credential-meta">
            <div>
              <label>Crawl Status</label>
              <strong>{form.mybinaSyncStatus}</strong>
            </div>

            <div>
              <label>Last Crawl Time</label>
              <strong>{form.mybinaLastSyncAt}</strong>
            </div>
          </div>

          <div className="setting-row mybina-status-control">
            <div className="setting-row__content">
              <h3>myBina Sync Status</h3>
              <p>Enable or disable manual and scheduled myBina project synchronization.</p>
            </div>

            <div className="status-toggle">
              <strong>{form.mybinaEnabled ? "Enabled" : "Disabled"}</strong>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={form.mybinaEnabled}
                  onChange={(e) => handleChange("mybinaEnabled", e.target.checked)}
                />
                <span className="slider" />
              </label>
            </div>
          </div>

          <div className="credential-grid">
            <label>
              Username
              <input
                type="text"
                placeholder="Enter myBina username"
                value={form.mybinaUsername}
                onChange={(e) => handleChange("mybinaUsername", e.target.value)}
              />
            </label>

            <label>
              Password
              <input
                type="password"
                placeholder="Enter myBina password"
                value={form.mybinaPassword}
                onChange={(e) => handleChange("mybinaPassword", e.target.value)}
              />
            </label>
          </div>

          <div className="settings-card__footer">
            <button type="submit" className="save-button" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
